import { SkeletonLoader, TooltipTrigger } from '@toggle/design-system';
import React from 'react';

import { TooltipContainer } from '~/components/tooltip-container/TooltipContainer';
import { PosthogTrackingSource } from '~/services/tracking/posthog/constants';
import { CatalystInsightIcon } from '~/widgets/catalyst-insights-drawer/catalyst-insight-icon/CatalystInsightIcon';
import { EarningsInsightIcon } from '~/widgets/earnings-insight-drawer/earnings-insight-widget/components/earnings-insight-icon/EarningsInsightIcon';

import { ClickableCatalystInsightTooltip } from '../clickable-catalyst-insight-tooltip/ClickableCatalystInsightTooltip';
import { useInsightsCellData } from '../hooks/useInsightsCellData';
import * as S from './InsightsKGCell.styles';

const MAX_KG_INSIGHTS = 2;

export type InsightsKGCellProps = {
  entityTag: string;
  isDisabled: boolean;
  source: PosthogTrackingSource;
};

export const InsightsKGCell = ({
  entityTag,
  isDisabled,
  source,
}: InsightsKGCellProps) => {
  const {
    isLoading,
    filteredCatalystInsights = [],
    earningsInsight,
  } = useInsightsCellData({ entityTag });

  if (isLoading) {
    return (
      <SkeletonLoader
        areas={[
          {
            styles: {
              height: '20px',
              borderRadius: '8px',
            },
          },
        ]}
      />
    );
  }

  const visibleCatalystInsights =
    filteredCatalystInsights.length >= MAX_KG_INSIGHTS
      ? filteredCatalystInsights.slice(0, MAX_KG_INSIGHTS - 1)
      : filteredCatalystInsights.slice(0, MAX_KG_INSIGHTS);

  const remainingCatalystInsights =
    filteredCatalystInsights.length >= MAX_KG_INSIGHTS
      ? filteredCatalystInsights.slice(MAX_KG_INSIGHTS - 1)
      : filteredCatalystInsights.slice(MAX_KG_INSIGHTS);

  return (
    <S.InsightsContainer>
      {earningsInsight ? (
        <EarningsInsightIcon
          entityTag={entityTag}
          earningsInsight={earningsInsight}
          width={18}
          height={20}
          source={source}
          isDisabled={isDisabled}
        />
      ) : (
        <S.Placeholder />
      )}
      {visibleCatalystInsights.map(catalystInsight => (
        <CatalystInsightIcon
          key={catalystInsight.id}
          catalystInsightId={catalystInsight.id}
          isBordered
          customSize={{ width: 18, height: 20 }}
          type="company"
          identifier={entityTag}
          isDisabled={isDisabled}
          source={source}
        />
      ))}
      {!!remainingCatalystInsights.length && (
        <TooltipContainer
          label={
            <ClickableCatalystInsightTooltip
              insights={remainingCatalystInsights}
              category={remainingCatalystInsights[0].category}
              source={source}
            />
          }
          inPortal
          trigger={[TooltipTrigger.Hover]}
          placement="top-end"
          withSafePolygon
          disabled={isDisabled}
        >
          <S.Count $isDisabled={isDisabled}>
            +{remainingCatalystInsights.length}
          </S.Count>
        </TooltipContainer>
      )}
    </S.InsightsContainer>
  );
};
